import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useRouter } from 'next/router';
import Script from 'next/script';

import Text from '../../Text';
import { Form, FormCircularProgress, FormWrapper, Root } from '../shared/components/BaseComponents';
import { KnownVisitor } from '../shared/components/KnownVisitor';
import { sidekick } from '../../../utils/sidekick';
import { addFieldModifiers } from '../shared/utils/addFieldModifiers';
import { isEmailGood, invalidEmailErrorMessage } from '../shared/utils/isEmailGood';
import {
  clearSubmittedFormData,
  readSubmittedFormData,
  storeSubmittedFormData,
  STORABLE_FIELDS,
} from '../shared/utils/knownVisitor';
import { logFormSubmitEvent } from '../shared/utils/tracking';

import type { BaseLeadFormProps } from '../shared/types';

export type FormMarketoEmbedProps = BaseLeadFormProps;
type MarketForm = any;

const clearFormFields = (form: MarketForm | undefined) => {
  if (form) {
    const entries = STORABLE_FIELDS.map((field) => [field, '']);
    form.setValues(Object.fromEntries(entries));
  }
};

const SCROLL_MARGIN_TOP = 64 + 50 + 50; // Sticky header (64) + padding of the parent container (50) + breathing room (50)

export const FormMarketoEmbed = ({
  baseUrl,
  munchkinId,
  formId,
  introText,
  successMessage,
  sidekickLookup,
  variant,
}: FormMarketoEmbedProps) => {
  const { locale } = useRouter();
  const formRef = useRef<MarketForm | null>(null);
  const successWrapperRef = useRef<HTMLDivElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [formValuesFromCookie, setFormValuesFromCookie] = useState(() => readSubmittedFormData());

  // Load and initialize Marketo form
  useEffect(() => {
    if (window.MktoForms2) {
      window.MktoForms2.loadForm(baseUrl, munchkinId, formId, addFieldModifiers);
      window.MktoForms2.whenReady((form: MarketForm) => {
        setIsLoading(false);

        // Store the form instance in a ref to be able to clear form fields
        formRef.current = form;

        // Populate form field values from the cookie, if available
        form.setValues(formValuesFromCookie);

        form.onValidate(() => {
          const email = form.vals().Email;
          if (!isEmailGood(email)) {
            form.submitable(false);

            // @ts-expect-error We don't have correct types for locale
            const errorMessage = invalidEmailErrorMessage[locale] ?? invalidEmailErrorMessage['en-US'];
            const emailElem = form.getFormElem().find('#Email');
            form.showErrorMessage(errorMessage, emailElem);
          } else {
            form.submitable(true);
          }
        });

        form.onSuccess(() => {
          setSuccess(true);
          logFormSubmitEvent(form.getId());

          // Store selected form field values in a cookie
          storeSubmittedFormData(form.getValues());

          return false;
        });
      });
    }
  }, [loaded]);

  // Scroll to the success message after the form is successfully submitted.
  //
  // This is needed because if the form is long, on smaller screens
  // the success message might be displayed above the viewport.
  useEffect(() => {
    if (success && successWrapperRef.current) {
      const element = successWrapperRef.current;
      const rect = element.getBoundingClientRect();
      const isTopEdgeInViewport = rect.top >= SCROLL_MARGIN_TOP && rect.top <= window.innerHeight;

      // Don't scroll if the top edge of the success message is already in the viewport
      if (!isTopEdgeInViewport) {
        successWrapperRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [success]);

  const clearFormValuesFromCookie = () => {
    clearFormFields(formRef.current);
    clearSubmittedFormData();
    setFormValuesFromCookie({}); // Update UI to not show the "Welcome back" message
  };

  return (
    <Root {...sidekick(sidekickLookup)} data-testid="FormMarketoEmbed" variant={variant ?? 'default'}>
      <Script
        strategy="afterInteractive"
        id="marketo"
        src={`${baseUrl}/js/forms2/js/forms2.min.js`}
        onError={(e) => {
          console.error('Script failed to load', e);
        }}
        onLoad={() => {
          setLoaded(true);
        }}
      />

      <FormWrapper hidden={success} data-testid="FormMarketoEmbed-formWrapper" className={variant}>
        {introText && <IntroText {...introText} />}

        {!isLoading && formValuesFromCookie.FirstName ? (
          <KnownVisitor formValues={formValuesFromCookie} onClearFormValues={clearFormValuesFromCookie} />
        ) : null}

        {isLoading && <FormCircularProgress size={40} data-testid="FormMarketoEmbed-circularProgress" />}
        <Form id={`mktoForm_${formId}`} data-testid="FormMarketoEmbed-form" />
      </FormWrapper>

      <SuccessWrapper hidden={!success} data-testid="FormMarketoEmbed-successWrapper" ref={successWrapperRef}>
        {successMessage && <Text {...successMessage} />}
      </SuccessWrapper>
    </Root>
  );
};

export default FormMarketoEmbed;

const IntroText = styled(Text, {
  name: 'FormMarketoEmbed',
  slot: 'IntroText',
})(() => ({
  '& h2:first-of-type[class*="MuiTypography-root"]': {
    paddingBottom: 0,
  },
  '& h4:first-of-type[class*="MuiTypography-root"]': {
    paddingBottom: 24,
  },
}));

const SuccessWrapper = styled(Box, {
  name: 'FormMarketoEmbed',
  slot: 'SuccessWrapper',
  shouldForwardProp: (prop) => prop !== 'variant' && prop !== 'hidden',
  overridesResolver: (_, styles) => styles.root,
})<{ variant?: string; hidden?: boolean }>(({ theme, hidden }) => ({
  display: hidden ? 'none !important' : 'block',
  // Adjust the behavior of scrollIntoView
  scrollMarginTop: SCROLL_MARGIN_TOP,
  '& img': {
    width: '40%',
  },
  // Overrides just don't end...
  // Make sure links in the success message stand out
  '& [class*="Text-root"] a, & [class*="Text-root"] a *': {
    color: `${theme.palette.teal.main} !important`,
    cursor: 'pointer',
  },
  "& [class*='MuiButton-contained']": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    '&:hover': {
      backgroundColor: `${theme.palette.secondary.main}80`,
    },
  },
}));
